import { AriaCapacitor } from '@codeus/aria-capacitor';
import Device from '@/wrappers/Device';
import i18n from '@/i18n';

export default class AndroidFiscalService {

    constructor() {
        this.certAlias = null
        this.pac = null
        this.sdcUrl = null
        this.sdc = null
    }

    async init(options = {}) {
        const { esdcUrl } = options;

        this.sdcUrl = esdcUrl
        this.certAlias = await AppStorage.native().getItem('certAlias')
        this.pac = await AppStorage.native().getItem('pac')

        if(!this.sdc) {
            this.sdc = AriaCapacitor
        }

        // Check if device is supported
        const supportedDevices = [
            { manufacturer: 'szzcs', model: 'Z91Pro' },
            { manufacturer: 'ZCS', model: 'Z91Pro' },
            { manufacturer: 'szzcs', model: 'Z100' },
            { manufacturer: 'ZCS', model: 'Z100' },
            { manufacturer: 'szzcs', model: 'digitron ist Z91' },
            { manufacturer: 'ZCS', model: 'digitron ist Z91' },
            { manufacturer: 'digitron ist', model: 'digitron ist Z91' },
            { manufacturer: 'Ciontek', model: 'CS20' },
            { manufacturer: 'Ciontek', model: 'CS30' },
            { manufacturer: 'BLESSX', model: 'any' },
        ]

        const device = Device.get()
        const manufacturer = device.manufacturer
        const model = device.model

        if (!supportedDevices.some(device => device.manufacturer === manufacturer && (device.model === 'any' || device.model === model))){
            console.log('ESDC not supported on this device')
            this.deviceSupported = false
            return
        }

        this.deviceSupported = true
        this.permissionsAllowed = await this.checkPermissions()

        if(this.permissionsAllowed) {
            const options = { sdc: 'ESDC', url: this.getSdcUrl('ESDC') };
            this.sdc.initEsdc({ options })
        }
    }

    async verifyPin(pin, sdc) {
        if(sdc === 'ESDC' && !this.deviceSupported) return { error: { code: 'not_supported_on_device', message: i18n.t('sdc.status.not_supported_on_device') } }
        if(sdc === 'ESDC' && !this.permissionsAllowed) return { error: { code: 'missing_permissions', message: i18n.t('sdc.status.missing_permissions') } }

        if(sdc === 'VSDC') {
            return { error: { code: 'not_supported' } }
        }

        try {
            const options = {
                url: this.getSdcUrl(sdc),
                pac: this.pac,
                certAlias: this.certAlias,
                sdc: sdc,
            }

            const response = await this.sdc.verifyPin({ data: pin, options: options })

            if(response.error) {
                return { error: this.formatError(response.error) }
            }

            return { response: this.formatResponse(response) }
        } catch (error) {
            return { error: this.formatError(error) }
        }
    }

    async createInvoice(invoice, sdc) {
        if(sdc === 'ESDC' && !this.deviceSupported) return { error: { code: 'not_supported_on_device', message: i18n.t('sdc.status.not_supported_on_device') } }
        if(sdc === 'ESDC' && !this.permissionsAllowed) return { error: { code: 'missing_permissions', message: i18n.t('sdc.status.missing_permissions') } }

        try {
            const options = {
                url: this.getSdcUrl(sdc),
                pac: this.pac,
                certAlias: this.certAlias,
                sdc: sdc,
            }

            const response = await this.sdc.createInvoice({ data: invoice, options: options })

            if(response.error) {
                return { error: this.formatError(response.error) }
            }

            return { response: this.formatResponse(response) }
        } catch (error) {
            return { error: this.formatError(error) }
        }
    }

    async getInvoice(requestId, sdc) {
        if(sdc === 'ESDC' && !this.deviceSupported) return { error: { code: 'not_supported_on_device', message: i18n.t('sdc.status.not_supported_on_device') } }
        if(sdc === 'ESDC' && !this.permissionsAllowed) return { error: { code: 'missing_permissions', message: i18n.t('sdc.status.missing_permissions') } }

        try {
            const options = {
                url: this.getSdcUrl(sdc),
                pac: this.pac,
                certAlias: this.certAlias,
                sdc: sdc,
            }

            const response = await this.sdc.getInvoice({ data: requestId, options: options })

            if(response.error) {
                return { error: this.formatError(response.error) }
            }

            return { response: this.formatResponse(response) }
        } catch (error) {
            return { error: this.formatError(error) }
        }
    }

    async getEnvironmentParameters(sdc) {
        if(sdc === 'ESDC' && !this.deviceSupported) return { error: { code: 'not_supported_on_device', message: i18n.t('sdc.status.not_supported_on_device') } }
        if(sdc === 'ESDC' && !this.permissionsAllowed) return { error: { code: 'missing_permissions', message: i18n.t('sdc.status.missing_permissions') } }

        try {
            const options = {
                url: this.getSdcUrl(sdc),
                pac: this.pac,
                certAlias: this.certAlias,
                sdc: sdc,
            }

            const response = await this.sdc.getEnvironmentParameters({ options })

            if(response.error) {
                return { error: this.formatError(response.error) }
            }

            return { response: this.formatResponse(response) }
        } catch (error) {
            return { error: this.formatError(error) }
        }
    }

    async getStatus(sdc) {
        if(sdc === 'ESDC' && !this.deviceSupported) return { error: { code: 'not_supported_on_device', message: i18n.t('sdc.status.not_supported_on_device') } }
        if(sdc === 'ESDC' && !this.permissionsAllowed) return { error: { code: 'missing_permissions', message: i18n.t('sdc.status.missing_permissions') } }

        try {
            const options = {
                url: this.getSdcUrl(sdc),
                pac: this.pac,
                certAlias: this.certAlias,
                sdc: sdc,
            }

            const response = await this.sdc.getStatus({ options })

            if(response.error) {
                return { error: this.formatError(response.error) }
            }

            return { response: this.formatResponse(response) }
        } catch (error) {
            return { error: this.formatError(error) }
        }
    }

    async getAttention(sdc) {
        if(sdc === 'ESDC' && !this.deviceSupported) return { error: { code: 'not_supported_on_device', message: i18n.t('sdc.status.not_supported_on_device') } }
        if(sdc === 'ESDC' && !this.permissionsAllowed) return { error: { code: 'missing_permissions', message: i18n.t('sdc.status.missing_permissions') } }

        try {
            const options = {
                url: this.getSdcUrl(sdc),
                pac: this.pac,
                certAlias: this.certAlias,
                sdc: sdc,
            }

            const response = await this.sdc.getAttention({ options })

            if(response.error) {
                return { error: this.formatError(response.error) }
            }

            return { response: this.formatResponse(response) }
        } catch (error) {
            return { error: this.formatError(error) }
        }
    }

    async getUiData(sdc) {
        if(sdc === 'ESDC' && !this.deviceSupported) return { error: { code: 'not_supported_on_device', message: i18n.t('sdc.status.not_supported_on_device') } }
        if(sdc === 'ESDC' && !this.permissionsAllowed) return { error: { code: 'missing_permissions', message: i18n.t('sdc.status.missing_permissions') } }

        if(sdc !== 'ESDC') {
            return { error: { code: 'supported_only_on_esdc' } }
        }

        try {
            const options = {
                url: this.getSdcUrl(sdc),
                pac: this.pac,
                certAlias: this.certAlias,
                sdc: sdc,
            }

            const response = await this.sdc.getUiData({ options })

            if(response.error) {
                return { error: this.formatError(response.error) }
            }

            return { response: this.formatResponse(response) }
        } catch (error) {
            return { error: this.formatError(error) }
        }
    }

    async storeLocalAuditFiles(sdc) {
        if(sdc === 'ESDC' && !this.deviceSupported) return { error: { code: 'not_supported_on_device', message: i18n.t('sdc.status.not_supported_on_device') } }
        if(sdc === 'ESDC' && !this.permissionsAllowed) return { error: { code: 'missing_permissions', message: i18n.t('sdc.status.missing_permissions') } }

        if(sdc !== 'ESDC') {
            return { error: { code: 'supported_only_on_esdc' } }
        }

        try {
            const options = {
                url: this.getSdcUrl(sdc),
                pac: this.pac,
                certAlias: this.certAlias,
                sdc: sdc,
            }

            const response = await this.sdc.storeLocalAuditFiles({ options })

            if(response.error) {
                return { error: this.formatError(response.error) }
            }

            return { response: this.formatResponse(response) }
        } catch (error) {
            return { error: this.formatError(error) }
        }
    }

    async executeCommandsFromStorage(sdc) {
        if(sdc === 'ESDC' && !this.deviceSupported) return { error: { code: 'not_supported_on_device', message: i18n.t('sdc.status.not_supported_on_device') } }
        if(sdc === 'ESDC' && !this.permissionsAllowed) return { error: { code: 'missing_permissions', message: i18n.t('sdc.status.missing_permissions') } }

        if(sdc !== 'ESDC') {
            return { error: { code: 'supported_only_on_esdc' } }
        }

        try {
            const options = {
                url: this.getSdcUrl(sdc),
                pac: this.pac,
                certAlias: this.certAlias,
                sdc: sdc,
            }

            const response = await this.sdc.executeCommandsFromStorage({ options })

            if(response.error) {
                return { error: this.formatError(response.error) }
            }

            return { response: this.formatResponse(response) }
        } catch (error) {
            return { error: this.formatError(error) }
        }
    }

    async chooseCertificate(password, pac) {
        const options = {
            url: this.getSdcUrl('VSDC'),
            pac: pac,
            certAlias: null,
            sdc: 'VSDC',
        }

        const certSelectResult = await this.sdc.chooseCertificate({ options })

        if(!certSelectResult.error) {
            const alias = certSelectResult.data;
            options.certAlias = alias

            const statusResult = await this.sdc.getStatus({ options })
            if(!statusResult.error) {
                AppStorage.native().setItem('pac', pac)
                AppStorage.native().setItem('certAlias', alias)

                this.pac = pac
                this.certAlias = alias

                return { success: true }
            }
        }

        return { success: false }
    }

    formatResponse(response) {
        return { data: response.data }
    }

    formatError(error = {}) {
        return { code: error.code || 'exception', message: error.message, response: { data: error.response ? error.response.data : null } }
    }

    getSdcUrl(sdc) {
        if(sdc === 'VSDC') return process.env.VUE_APP_VSDC_URL
        else if(sdc === 'ESDC') return this.sdcUrl || process.env.VUE_APP_ESDC_URL
        else if(sdc === 'DEVSDC') return process.env.VUE_APP_ESDC_DEV_URL

        return null
    }

    async checkPermissions() {
        let permissions = await this.sdc.checkPermissions();

        if(permissions.esdc !== 'granted') {
            permissions = await this.sdc.requestPermissions();
        }

        return permissions.esdc === 'granted';
    }
}
